import React from 'react';
import { Message, User } from '../../types';
import clsx from 'clsx';
import { format } from 'date-fns';
import { FileText, Download, Check } from 'lucide-react';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  sender?: User;
  users: User[];
  onImageClick: (url: string) => void;
}

const URL_REGEX = /(https?:\/\/[^\s<>"{}|\\^`[\]]+)/g;

function renderContent(text: string, isOwn: boolean) {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx('underline break-all', isOwn ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800')}
        onClick={e => e.stopPropagation()}
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn, sender, users, onImageClick }) => {
  const readByUsers = users.filter(u => {
    if (u.id === message.senderId) return false;
    const lastSeenTime = u.lastSeen || 0;
    const lastSignIn = u.lastSignInTime || 0;
    return lastSeenTime >= message.timestamp || lastSignIn >= message.timestamp;
  });

  const readByTitle = readByUsers.length > 0 ? `Read by ${readByUsers.map(u => u.displayName).join(', ')}` : '';

  return (
    <div className={clsx("flex w-full mb-4", isOwn ? "justify-end" : "justify-start")}>
      <div className={clsx("flex max-w-[75%] md:max-w-[60%]", isOwn ? "flex-row-reverse" : "flex-row")}>
        {!isOwn && (
          <div className="flex-shrink-0 mr-2 mt-auto">
            {sender ? (
              <img src={sender.photoURL} alt={sender.displayName} className="w-8 h-8 rounded-full object-cover border border-gray-200" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gray-300" />
            )}
          </div>
        )}
        <div className={clsx("flex flex-col p-3 rounded-2xl shadow-sm", isOwn ? "bg-blue-600 text-white rounded-br-none" : "bg-white text-gray-800 border border-gray-100 rounded-bl-none")}>
          {!isOwn && sender && <span className="text-xs font-semibold text-gray-500 mb-1 ml-1">{sender.displayName}</span>}
          {message.attachments && message.attachments.length > 0 && (
            <div className="space-y-2 mb-2">
              {message.attachments.map((att) => (
                <div key={att.id} className="overflow-hidden rounded-lg">
                  {att.type === 'image' ? (
                    <img
                      src={att.url}
                      alt="attachment"
                      className="max-w-full h-auto max-h-64 object-cover rounded-lg cursor-zoom-in"
                      onClick={() => onImageClick(att.url)}
                    />
                  ) : (
                    <a href={att.url} download={att.name} className={clsx("flex items-center gap-3 p-3 rounded-lg transition-colors", isOwn ? "bg-blue-700 hover:bg-blue-800" : "bg-gray-100 hover:bg-gray-200")}>
                      <div className={clsx("p-2 rounded-full", isOwn ? "bg-blue-600" : "bg-white")}><FileText className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{att.name}</p></div>
                      <Download className="w-4 h-4 opacity-70" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
          {message.content && (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {renderContent(message.content, isOwn)}
            </p>
          )}
          <div className="flex items-center justify-between">
            <span className={clsx("text-[10px] mt-1 self-end opacity-70", isOwn ? "text-blue-100" : "text-gray-400")}>{format(message.timestamp, 'h:mm a')}</span>
            {readByUsers.length > 0 && (
              <div className="has-tooltip">
                <Check className="w-4 h-4 text-blue-300" />
                <div className='tooltip'>{readByTitle}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
